/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import type { DiagramNode, DiagramLink, Bounds, ItemInfo } from '../Diagram.types';

/**
 * Navigation direction
 */
export type NavDirection = 'right' | 'left' | 'up' | 'down';

type linkDetailType<K2> = {
  id: K2;
  angle: number;
  distance: number;
  /**
   * Ingoing = 1, outgoing = 0
   */
  direction: 0 | 1;
};

/**
 * Returns the node data based on node info in a diagram.
 * @param itemInfo
 * @returns
 */
export function getNodeDetailFromInfo<K1, K2>(
  itemInfo: ItemInfo<K1, K2>,
  nodes: DiagramNode<K1>[]
): DiagramNode<K1> | undefined {
  let node;
  for (let i = 0; i < nodes.length; i++) {
    node = nodes[i];
    if (itemInfo.id === node.id) {
      return node;
    }
  }
  return undefined;
}

/**
 * Returns the link data based on link info in a diagram.
 * @param itemInfo
 * @returns
 */
export function getLinkDetailFromInfo<K1, K2>(
  itemInfo: ItemInfo<K1, K2>,
  links: DiagramLink<K2, K1>[]
): DiagramLink<K2, K1> | undefined {
  let link;
  for (let i = 0; i < links.length; i++) {
    link = links[i];
    if (itemInfo.id === link.id) {
      return link;
    }
  }
  return undefined;
}

/**
 * Creates a map with key node id and value node bounds
 */
export function createNodeBoundsMap<K1>(nodes: DiagramNode<K1>[], bounds: Bounds[]) {
  const nodeBounds: Map<K1, Bounds> = new Map();
  for (let i = 0; i < nodes.length; i++) {
    nodeBounds.set(nodes[i].id, bounds[i]);
  }
  return nodeBounds;
}

/**
 * Utility method that adds sorting attributes of each link to an array
 */
export function addSortingAttributes<K1, K2>(
  node: DiagramNode<K1>,
  listOfLinks: DiagramLink<K2, K1>[],
  nodes: DiagramNode<K1>[],
  nodeBounds: Map<K1, Bounds>
) {
  let angle, distance, direction;
  const linkDetail: linkDetailType<K2>[] = [];
  for (let i = 0; i < listOfLinks.length; i++) {
    const link = listOfLinks[i];
    angle = _getClockwiseAngle(node, link, nodes, nodeBounds);
    distance = _getNodesDistance(link, nodes, nodeBounds);
    direction = _getLinkDirection(node, link);
    linkDetail.push({ id: link.id, angle: angle, distance: distance, direction: direction });
  }
  return linkDetail;
}

/**
 * Returns a function that compares two link around a given node
 * The links are analyzed by angle, distance from the node and direction. The sorting attributes are added to the links before sorting.
 */
export function getLinkComparator<K2>() {
  return (link1: linkDetailType<K2>, link2: linkDetailType<K2>) => {
    const { angle: linkAngle1, distance: linkDistance1, direction: linkDirection1 } = link1;
    const { angle: linkAngle2, distance: linkDistance2, direction: linkDirection2 } = link2;
    let res = -1;

    if (!_anglesAreEqualWithinTolerance(linkAngle1, linkAngle2) && linkAngle1 > linkAngle2) {
      res = 1;
    } else if (_anglesAreEqualWithinTolerance(linkAngle1, linkAngle2)) {
      //check distance and direction
      if (linkDistance1 > linkDistance2) {
        res = 1;
      } else if (linkDistance2 == linkDistance1 && linkDirection1 > linkDirection2) {
        //outgoing to ingoing
        res = 1;
      } else if (linkDistance2 == linkDistance1 && linkDirection1 == linkDirection2) {
        res = 0;
      }
    }
    return res;
  };
}

/**
 * Returns navigable links for a given node
 */
export function getNavigableLinksForNodeId<K1, K2>(nodeId: K1, listOfLinks: DiagramLink<K2, K1>[]) {
  const links: DiagramLink<K2, K1>[] = [];
  listOfLinks.forEach((link) => {
    const startId = link.startNode;
    const endId = link.endNode;

    if (startId == nodeId || endId == nodeId) links.push(link);
  });
  return links;
}

/**
 * Get the clockwise angle for the link given node as a center
 */
function _getClockwiseAngle<K1, K2>(
  node: DiagramNode<K1>,
  link: DiagramLink<K2, K1>,
  nodes: DiagramNode<K1>[],
  nodeBounds: Map<K1, Bounds>
) {
  //find opposite node
  let currNode;
  let startNode = nodes[0];
  let endNode = nodes[0];
  for (let i = 0; i < nodes.length; i++) {
    currNode = nodes[i];
    if (currNode.id === link.startNode) {
      startNode = currNode;
    } else if (currNode.id === link.endNode) {
      endNode = currNode;
    }
  }
  const oppositeNode = node.id == startNode.id ? endNode : startNode;

  const p1 = _getNodeCenter(node, nodeBounds);
  const p2 = _getNodeCenter(oppositeNode, nodeBounds);
  let angle = Math.atan2(p2.y - p1.y, p2.x - p1.x);
  angle = angle < 0 ? angle + Math.PI * 2 : angle;
  return angle;
}

/**
 * Get the distance between start and end nodes for the given link
 */
function _getNodesDistance<K1, K2>(
  link: DiagramLink<K2, K1>,
  nodes: DiagramNode<K1>[],
  nodeBounds: Map<K1, Bounds>
) {
  let currNode;
  let startNode = nodes[0];
  let endNode = nodes[0];
  for (let i = 0; i < nodes.length; i++) {
    currNode = nodes[i];
    if (currNode.id === link.startNode) {
      startNode = currNode;
    } else if (currNode.id === link.endNode) {
      endNode = currNode;
    }
  }
  const p1 = _getNodeCenter(startNode, nodeBounds);
  const p2 = _getNodeCenter(endNode, nodeBounds);
  return Math.sqrt(Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2));
}

/**
 * Get link direction for the given node.  1 if the node is the end node
 * and 0 if it is the start node
 */
function _getLinkDirection<K1, K2>(node: DiagramNode<K1>, link: DiagramLink<K2, K1>): 0 | 1 {
  if (link.endNode === node.id) {
    return 1;
  }
  return 0;
}

/**
 * Get node center
 */
function _getNodeCenter<K1>(node: DiagramNode<K1>, nodeBounds: Map<K1, Bounds>) {
  const nodeBB = nodeBounds.get(node.id)!;
  return { x: nodeBB.x + nodeBB.w / 2, y: nodeBB.y + nodeBB.h / 2 };
}

/**
 * Calculates the angle weighted by distance
 */
export function calcDistanceAngleWeighted(
  objectBB: Bounds,
  currentBB: Bounds,
  direction: NavDirection
) {
  // Variables used for calculating penalties when calculating distances between two DvtKeyboardNavigables
  const optimalAngle1 = (15 / 180) * Math.PI;
  const optimalAngle2 = (40 / 180) * Math.PI;
  const suboptimalAnglePenalty1 = 2; // multiplier to the distance
  const suboptimalAnglePenalty2 = 6; // multiplier to the distance

  const objCenterX = objectBB.x + objectBB.w / 2;
  const objCenterY = objectBB.y + objectBB.h / 2;

  const curCenterX = currentBB.x + currentBB.w / 2;
  const curCenterY = currentBB.y + currentBB.h / 2;

  const x_dist = Math.abs(objCenterX - curCenterX);
  const y_dist = Math.abs(objCenterY - curCenterY);

  const angle = Math.atan2(y_dist, x_dist);

  let distance = Math.sqrt(x_dist * x_dist + y_dist * y_dist);

  // Angle penalty based on direction
  if (
    (angle > optimalAngle1 && (direction == 'right' || direction == 'left')) ||
    (angle < Math.PI / 2 - optimalAngle1 && (direction == 'up' || direction == 'down'))
  ) {
    if (
      (angle > optimalAngle2 && (direction == 'right' || direction == 'left')) ||
      (angle < Math.PI / 2 - optimalAngle2 && (direction == 'up' || direction == 'down'))
    ) {
      distance *= suboptimalAnglePenalty2;
    } else {
      distance *= suboptimalAnglePenalty1;
    }
  }

  return distance;
}

/**
 * Given a direction, is a certain node is a valid destination to navigate to
 */
export function isValidDestination(
  objBB: Bounds,
  curBB: Bounds,
  direction: NavDirection,
  compareCenters: boolean
) {
  // compare the centers of the navigable and to be valid, the navigable must be in the right direction
  // without tolerance
  if (compareCenters) {
    const objCenterX = objBB.x + 0.5 * objBB.w;
    const curCenterX = curBB.x + 0.5 * curBB.w;
    const objCenterY = objBB.y + 0.5 * objBB.h;
    const curCenterY = curBB.y + 0.5 * curBB.h;

    switch (direction) {
      case 'up':
        return objCenterY < curCenterY;
      case 'down':
        return objBB.y > curCenterY;
      case 'right':
        return objCenterX > curCenterX;
      case 'left':
        return objCenterX < curCenterX;
      default:
        break;
    }
    return true;
  }

  switch (direction) {
    case 'up':
      return objBB.y < curBB.y || _areEqualWithinTolerance(objBB.y, curBB.y);
    case 'down':
      return objBB.y > curBB.y || _areEqualWithinTolerance(objBB.y, curBB.y);
    case 'right':
      return objBB.x > curBB.x || _areEqualWithinTolerance(objBB.x, curBB.x);
    case 'left':
      return objBB.x < curBB.x || _areEqualWithinTolerance(objBB.x, curBB.x);
    default:
      break;
  }
  return true;
}

/**
 * Returns true if two nodes are in contact
 */
export function calcInContact(objRect: Bounds, curRect: Bounds, direction: NavDirection) {
  switch (direction) {
    case 'up':
      return (
        _isVerticallyAligned(objRect, curRect) &&
        (curRect.y <= objRect.y + objRect.h ||
          _areEqualWithinTolerance(curRect.y, objRect.y + objRect.h))
      );
    case 'down':
      return (
        _isVerticallyAligned(objRect, curRect) &&
        (objRect.y <= curRect.y + curRect.h ||
          _areEqualWithinTolerance(objRect.y, curRect.y + curRect.h))
      );
    case 'right':
      return (
        _isHorizontallyAligned(objRect, curRect) &&
        (objRect.x <= curRect.x + curRect.w ||
          _areEqualWithinTolerance(objRect.x, curRect.x + curRect.w))
      );
    case 'left':
      return (
        _isHorizontallyAligned(objRect, curRect) &&
        (curRect.x <= objRect.x + objRect.w ||
          _areEqualWithinTolerance(curRect.x, objRect.x + objRect.w))
      );
    default:
      break;
  }

  return false;
}

function _isVerticallyAligned(rect1: Bounds, rect2: Bounds) {
  return (
    (rect1.x >= rect2.x && rect1.x <= rect2.x + rect2.w) ||
    (rect2.x >= rect1.x && rect2.x <= rect1.x + rect1.w)
  );
}

function _isHorizontallyAligned(rect1: Bounds, rect2: Bounds) {
  return (
    (rect1.y >= rect2.y && rect1.y <= rect2.y + rect2.h) ||
    (rect2.y >= rect1.y && rect2.y <= rect1.y + rect1.h)
  );
}

function _areEqualWithinTolerance(a: number, b: number) {
  return Math.abs(a - b) <= 0.0000001;
}

function _anglesAreEqualWithinTolerance(a1: number, a2: number) {
  let res = Math.abs(a1 - a2) <= 0.0000001;
  if (!res) {
    res = Math.abs(Math.PI * 2 + Math.min(a1, a2) - Math.max(a1, a2)) <= 0.0000001;
  }
  return res;
}
